import React, { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import Loader from "../../components/Loader";
import Message from "../../components/Message";
import {
  createPromoCode,
  deletePromoCode,
  getPromoCodeDetails,
  updatePromoCode,
} from "../../actions/promoCodeActions";
import { listAdminProducts } from "../../actions/adminProductActions";
import {
  PROMO_CODE_CREATE_RESET,
  PROMO_CODE_DELETE_RESET,
  PROMO_CODE_UPDATE_RESET,
} from "../../constants/promoCodeConstants";

function toDateInputValue(value = "") {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString().slice(0, 10);
}

function normalizeCode(value = "") {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
}

function safeText(value = "") {
  return String(value ?? "").trim();
}

function getProductId(product = {}) {
  return safeText(product.id || product._id);
}

function getProductLabel(product = {}) {
  return safeText(product.name) || safeText(product.slug) || "Untitled product";
}

function getProductMeta(product = {}) {
  return [product.shopKey, product.sku, product.status]
    .map(safeText)
    .filter(Boolean)
    .join(" | ");
}

function getProductImage(product = {}) {
  const firstImage = Array.isArray(product.images) ? product.images[0] : null;

  return (
    safeText(product.defaultImage) ||
    safeText(firstImage?.url || firstImage?.secure_url)
  );
}

function normalizeProductIds(values = []) {
  if (!Array.isArray(values)) return [];

  const seen = new Set();
  const ids = [];

  values.forEach((value) => {
    const id = safeText(value);

    if (!id || seen.has(id)) return;

    seen.add(id);
    ids.push(id);
  });

  return ids;
}

export default function AdminPromoCodeFormScreen() {
  const { id } = useParams();
  const isEdit = Boolean(id);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const details = useSelector((state) => state.promoCodeDetails || {});
  const created = useSelector((state) => state.promoCodeCreate || {});
  const updated = useSelector((state) => state.promoCodeUpdate || {});
  const deleted = useSelector((state) => state.promoCodeDelete || {});
  const productList = useSelector((state) => state.adminProductList || {});

  const { loading: loadingDetails, error: errorDetails, promoCode } = details;
  const {
    success: successCreate,
    promoCode: createdPromoCode,
    error: errorCreate,
  } = created;
  const { success: successUpdate, error: errorUpdate } = updated;
  const { success: successDelete, error: errorDelete } = deleted;
  const {
    loading: loadingProducts,
    error: errorProducts,
    products = [],
  } = productList;

  const [code, setCode] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [discountType, setDiscountType] = useState("percent");
  const [discountValue, setDiscountValue] = useState(10);
  const [minimumSubtotal, setMinimumSubtotal] = useState(0);
  const [maxDiscount, setMaxDiscount] = useState(0);
  const [currency, setCurrency] = useState("GBP");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [usageLimit, setUsageLimit] = useState(0);
  const [perUserLimit, setPerUserLimit] = useState(0);
  const [isActive, setIsActive] = useState(true);
  const [productIds, setProductIds] = useState([]);
  const [productSearch, setProductSearch] = useState("");

  const isDeleted = Boolean(promoCode?.isDeleted);

  const title = useMemo(
    () => (isEdit ? "Edit Promo Code" : "Create Promo Code"),
    [isEdit],
  );

  useEffect(() => {
    if (isEdit) {
      dispatch(getPromoCodeDetails(id));
      return;
    }

    dispatch({ type: PROMO_CODE_CREATE_RESET });
    dispatch({ type: PROMO_CODE_UPDATE_RESET });
    dispatch({ type: PROMO_CODE_DELETE_RESET });
  }, [dispatch, id, isEdit]);

  useEffect(() => {
    const search = safeText(productSearch);
    const timer = setTimeout(() => {
      dispatch(
        listAdminProducts({
          status: "all",
          limit: 100,
          includeFacets: false,
          ...(search ? { search } : {}),
        }),
      ).catch(() => {});
    }, 250);

    return () => clearTimeout(timer);
  }, [dispatch, productSearch]);

  useEffect(() => {
    if (!isEdit || !promoCode) return;

    setCode(promoCode.code || "");
    setName(promoCode.name || "");
    setDescription(promoCode.description || "");
    setDiscountType(promoCode.discountType || "percent");
    setDiscountValue(Number(promoCode.discountValue || 0));
    setMinimumSubtotal(Number(promoCode.minimumSubtotal || 0));
    setMaxDiscount(Number(promoCode.maxDiscount || 0));
    setCurrency(promoCode.currency || "GBP");
    setStartsAt(toDateInputValue(promoCode.startsAt));
    setEndsAt(toDateInputValue(promoCode.endsAt));
    setUsageLimit(Number(promoCode.usageLimit || 0));
    setPerUserLimit(Number(promoCode.perUserLimit || 0));
    setIsActive(Boolean(promoCode.isActive));
    setProductIds(normalizeProductIds(promoCode.productIds));
  }, [isEdit, promoCode]);

  useEffect(() => {
    if (successCreate && createdPromoCode?.id) {
      dispatch({ type: PROMO_CODE_CREATE_RESET });
      navigate(`/admin/promo-codes/${createdPromoCode.id}`);
    }
  }, [createdPromoCode, dispatch, navigate, successCreate]);

  useEffect(() => {
    if (successUpdate) {
      dispatch({ type: PROMO_CODE_UPDATE_RESET });
      navigate("/admin/promo-codes");
    }
  }, [dispatch, navigate, successUpdate]);

  useEffect(() => {
    if (successDelete) {
      dispatch({ type: PROMO_CODE_DELETE_RESET });
      navigate("/admin/promo-codes");
    }
  }, [dispatch, navigate, successDelete]);

  const visibleProducts = useMemo(() => {
    const query = safeText(productSearch).toLowerCase();

    return products
      .filter((product) => getProductId(product))
      .filter((product) => {
        if (!query) return true;

        return [
          product.name,
          product.slug,
          product.sku,
          product.shopKey,
          product.status,
        ]
          .map(safeText)
          .join(" ")
          .toLowerCase()
          .includes(query);
      });
  }, [productSearch, products]);

  const selectedProductIds = useMemo(
    () => normalizeProductIds(productIds),
    [productIds],
  );

  const selectedProductSet = useMemo(
    () => new Set(selectedProductIds),
    [selectedProductIds],
  );

  const toggleProduct = (productId) => {
    const safeProductId = safeText(productId);

    if (!safeProductId) return;

    setProductIds((current) => {
      const currentIds = normalizeProductIds(current);

      if (currentIds.includes(safeProductId)) {
        return currentIds.filter((idValue) => idValue !== safeProductId);
      }

      return [...currentIds, safeProductId];
    });
  };

  const selectVisibleProducts = () => {
    setProductIds((current) =>
      normalizeProductIds([
        ...current,
        ...visibleProducts.map((product) => getProductId(product)),
      ]),
    );
  };

  const submitHandler = (event) => {
    event.preventDefault();

    const payload = {
      code: normalizeCode(code),
      name,
      description,
      discountType,
      discountValue: Number(discountValue || 0),
      minimumSubtotal: Number(minimumSubtotal || 0),
      maxDiscount: Number(maxDiscount || 0),
      currency,
      startsAt,
      endsAt,
      usageLimit: Number(usageLimit || 0),
      perUserLimit: Number(perUserLimit || 0),
      isActive,
      productIds: selectedProductIds,
    };

    if (isEdit) dispatch(updatePromoCode(id, payload));
    else dispatch(createPromoCode(payload));
  };

  const deleteHandler = () => {
    if (!isEdit || isDeleted) return;
    if (window.confirm("Delete this promo code?")) {
      dispatch(deletePromoCode(id));
    }
  };

  return (
    <Container className="py-4">
      <Row className="align-items-center g-3 mb-3">
        <Col>
          <h1 className="mb-1">{title}</h1>
          {isEdit ? (
            <div className="d-flex gap-2 flex-wrap">
              <Badge bg={isDeleted ? "secondary" : "dark"}>
                {isDeleted ? "Deleted" : "Active record"}
              </Badge>
              <Badge bg={isActive ? "success" : "warning"} text={isActive ? undefined : "dark"}>
                {isActive ? "Enabled" : "Disabled"}
              </Badge>
            </div>
          ) : null}
        </Col>

        <Col md="auto" className="d-flex gap-2 flex-wrap">
          <Link to="/admin/promo-codes" className="btn btn-light">
            Back
          </Link>
          {isEdit ? (
            <Button
              variant="outline-danger"
              onClick={deleteHandler}
              disabled={isDeleted}
            >
              Delete
            </Button>
          ) : null}
        </Col>
      </Row>

      {loadingDetails ? <Loader /> : null}
      {errorDetails ? <Message variant="danger">{errorDetails}</Message> : null}
      {errorCreate ? <Message variant="danger">{errorCreate}</Message> : null}
      {errorUpdate ? <Message variant="danger">{errorUpdate}</Message> : null}
      {errorDelete ? <Message variant="danger">{errorDelete}</Message> : null}

      {(!isEdit || promoCode) ? (
        <Form onSubmit={submitHandler}>
          <Row className="g-4">
            <Col lg={7}>
              <Card className="shadow-sm">
                <Card.Body>
                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Code</Form.Label>
                        <Form.Control
                          value={code}
                          onChange={(event) =>
                            setCode(normalizeCode(event.target.value))
                          }
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          value={name}
                          onChange={(event) => setName(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={description}
                      onChange={(event) => setDescription(event.target.value)}
                    />
                  </Form.Group>

                  <Row className="g-3">
                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Discount Type</Form.Label>
                        <Form.Select
                          value={discountType}
                          onChange={(event) =>
                            setDiscountType(event.target.value)
                          }
                        >
                          <option value="percent">Percentage</option>
                          <option value="fixed">Fixed amount</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>
                          {discountType === "fixed" ? "Amount" : "Percent"}
                        </Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={discountValue}
                          onChange={(event) =>
                            setDiscountValue(event.target.value)
                          }
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group className="mb-3">
                        <Form.Label>Currency</Form.Label>
                        <Form.Control
                          value={currency}
                          maxLength={3}
                          onChange={(event) =>
                            setCurrency(
                              event.target.value.toUpperCase().slice(0, 3),
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Minimum Subtotal</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={minimumSubtotal}
                          onChange={(event) =>
                            setMinimumSubtotal(event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Maximum Discount</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          step="0.01"
                          value={maxDiscount}
                          onChange={(event) =>
                            setMaxDiscount(event.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <div className="d-flex gap-2 flex-wrap mt-2">
                    <Button variant="dark" type="submit">
                      {isEdit ? "Save" : "Create"}
                    </Button>
                    <Link
                      to="/admin/promo-codes"
                      className="btn btn-outline-dark"
                    >
                      Cancel
                    </Link>
                  </div>
                </Card.Body>
              </Card>
            </Col>

            <Col lg={5}>
              <Card className="shadow-sm mb-4">
                <Card.Body>
                  <h5>Availability</h5>

                  <Row className="g-3">
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Starts</Form.Label>
                        <Form.Control
                          type="date"
                          value={startsAt}
                          onChange={(event) => setStartsAt(event.target.value)}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Ends</Form.Label>
                        <Form.Control
                          type="date"
                          value={endsAt}
                          onChange={(event) => setEndsAt(event.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Check
                    className="mb-2"
                    type="checkbox"
                    label="Enabled"
                    checked={isActive}
                    onChange={(event) => setIsActive(event.target.checked)}
                  />
                </Card.Body>
              </Card>

              <Card className="shadow-sm">
                <Card.Body>
                  <h5>Limits</h5>

                  <Form.Group className="mb-3">
                    <Form.Label>Total Usage Limit</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={usageLimit}
                      onChange={(event) => setUsageLimit(event.target.value)}
                    />
                  </Form.Group>

                  <Form.Group className="mb-0">
                    <Form.Label>Per-user Limit</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={perUserLimit}
                      onChange={(event) => setPerUserLimit(event.target.value)}
                    />
                  </Form.Group>
                </Card.Body>
              </Card>

              <Card className="shadow-sm mt-4">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center gap-2 mb-3">
                    <h5 className="mb-0">Products</h5>
                    <Badge bg={selectedProductIds.length ? "dark" : "secondary"}>
                      {selectedProductIds.length
                        ? `${selectedProductIds.length} selected`
                        : "All products"}
                    </Badge>
                  </div>

                  {errorProducts ? (
                    <Message variant="danger">{errorProducts}</Message>
                  ) : null}

                  <Form.Group className="mb-3">
                    <Form.Label>Search Products</Form.Label>
                    <Form.Control
                      value={productSearch}
                      onChange={(event) => setProductSearch(event.target.value)}
                      placeholder="Name, SKU, shop, status"
                    />
                  </Form.Group>

                  <div className="d-flex gap-2 flex-wrap mb-3">
                    <Button
                      type="button"
                      variant="outline-dark"
                      size="sm"
                      onClick={selectVisibleProducts}
                      disabled={!visibleProducts.length}
                    >
                      Select visible
                    </Button>
                    <Button
                      type="button"
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => setProductIds([])}
                      disabled={!selectedProductIds.length}
                    >
                      Clear
                    </Button>
                  </div>

                  {loadingProducts ? (
                    <div className="text-muted small mb-2">
                      Loading products...
                    </div>
                  ) : null}

                  <div
                    className="border rounded p-2"
                    style={{ maxHeight: 260, overflowY: "auto" }}
                  >
                    {visibleProducts.map((product) => {
                      const productId = getProductId(product);

                      return (
                        <Form.Check
                          key={productId}
                          id={`promo-product-${productId}`}
                          className="mb-2"
                          type="checkbox"
                          checked={selectedProductSet.has(productId)}
                          onChange={() => toggleProduct(productId)}
                          label={
                            <span className="d-flex align-items-center gap-3">
                              {getProductImage(product) ? (
                                <img
                                  src={getProductImage(product)}
                                  alt=""
                                  className="border rounded"
                                  style={{
                                    width: 48,
                                    height: 48,
                                    objectFit: "cover",
                                    flex: "0 0 auto",
                                  }}
                                />
                              ) : (
                                <span
                                  className="border rounded text-muted d-inline-flex align-items-center justify-content-center small"
                                  style={{
                                    width: 48,
                                    height: 48,
                                    flex: "0 0 auto",
                                  }}
                                >
                                  IMAGE
                                </span>
                              )}
                              <span className="d-block">
                                <span className="fw-semibold text-uppercase d-block">
                                  {getProductLabel(product)}
                                </span>
                                {getProductMeta(product) ? (
                                  <span className="text-muted d-block small text-uppercase">
                                    {getProductMeta(product)}
                                  </span>
                                ) : null}
                              </span>
                            </span>
                          }
                        />
                      );
                    })}

                    {!visibleProducts.length && !loadingProducts ? (
                      <div className="text-muted small">No products found.</div>
                    ) : null}
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Form>
      ) : null}
    </Container>
  );
}
